import React, { useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Typography,
  Upload
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import type { OperationalActionDescriptor } from '../routes/execution/executionActionsFacade';
import type { ExecutionCargoHydration } from './types';
import type { CargoQuantityUpdate, StopExecutionAction } from './types';
import {
  INCIDENT_TYPE_LABELS,
  isBlockingIncidentType,
  type ExecutionIncidentType
} from '../routes/execution/incidentFacade';

const { Text } = Typography;
const { TextArea } = Input;

export type ExecutionActionSubmitPayload = {
  descriptor: OperationalActionDescriptor;
  occurredAt: string;
  comment: string;
  cargoQuantities?: CargoQuantityUpdate[];
  incident?: {
    incidentType: ExecutionIncidentType;
    blockingFlag: boolean;
    downtimeHours?: number;
    comment: string;
  };
};

type Props = {
  open: boolean;
  descriptor: OperationalActionDescriptor | null;
  routeId: number;
  cargoAtStop: ExecutionCargoHydration[];
  onCancel: () => void;
  onSubmit: (payload: ExecutionActionSubmitPayload) => Promise<void>;
  loading?: boolean;
  defaultIncidentType?: ExecutionIncidentType;
};

export const ExecutionActionModal: React.FC<Props> = ({
  open,
  descriptor,
  routeId,
  cargoAtStop,
  onCancel,
  onSubmit,
  loading = false,
  defaultIncidentType
}) => {
  const [form] = Form.useForm();
  const [registerIncident, setRegisterIncident] = useState(false);
  const [incidentType, setIncidentType] = useState<ExecutionIncidentType>('downtime');

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        occurredAt: dayjs(),
        comment: '',
        partialQty: undefined
      });
      setRegisterIncident(descriptor?.opensIncident ?? false);
      setIncidentType(defaultIncidentType ?? 'downtime');
    }
  }, [open, descriptor, form, defaultIncidentType]);

  const title = descriptor?.opensIncident
    ? 'Проблема в пути'
    : descriptor?.label ?? 'Подтверждение операции';

  const showPartialQty = descriptor?.requiresPartialQty && cargoAtStop.length > 0;
  const isExecutionAction =
    !descriptor?.opensIncident &&
    (descriptor?.internalAction || (descriptor?.compositeActions?.length ?? 0) > 0);

  const incidentOptions = useMemo(
    () =>
      Object.entries(INCIDENT_TYPE_LABELS).map(([value, label]) => ({
        value,
        label
      })),
    []
  );

  const handleOk = async () => {
    if (!descriptor) return;
    const values = await form.validateFields();
    const dt: Dayjs = values.occurredAt;
    const occurredAt = dt.toISOString();
    const comment = String(values.comment ?? '').trim();

    let cargoQuantities: CargoQuantityUpdate[] | undefined;
    if (showPartialQty && values.partialQty != null) {
      cargoQuantities = cargoAtStop.map((c) => ({
        cargoId: c.cargoId,
        fromExecutionStopUid: c.fromExecutionStopUid,
        toExecutionStopUid: c.toExecutionStopUid,
        quantity: Number(values.partialQty)
      }));
    }

    let incident: ExecutionActionSubmitPayload['incident'];
    if (descriptor.opensIncident || registerIncident) {
      const type = (descriptor.opensIncident ? values.incidentType : incidentType) as ExecutionIncidentType;
      incident = {
        incidentType: type,
        blockingFlag: values.blockingFlag ?? isBlockingIncidentType(type),
        downtimeHours: values.downtimeHours != null ? Number(values.downtimeHours) : undefined,
        comment: comment || title
      };
    }

    if (descriptor.opensIncident) {
      await onSubmit({ descriptor, occurredAt, comment, incident });
      return;
    }

    if (!isExecutionAction) {
      await onSubmit({ descriptor, occurredAt, comment, incident });
      return;
    }

    await onSubmit({ descriptor, occurredAt, comment, cargoQuantities, incident });
  };

  return (
    <Modal
      open={open}
      title={title}
      onCancel={onCancel}
      onOk={() => void handleOk()}
      confirmLoading={loading}
      okText="Сохранить"
      cancelText="Отмена"
      destroyOnClose
      width={520}
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="occurredAt"
          label="Дата и время"
          rules={[{ required: true, message: 'Укажите дату и время' }]}
        >
          <DatePicker showTime format="DD.MM.YYYY HH:mm" style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="comment" label="Комментарий">
          <TextArea rows={3} placeholder="Комментарий логиста" maxLength={2000} />
        </Form.Item>

        {showPartialQty ? (
          <Form.Item
            name="partialQty"
            label="Фактически выгружено (шт.)"
            rules={[{ required: true, message: 'Укажите количество' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        ) : null}

        {descriptor?.opensIncident ? (
          <>
            <Form.Item
              name="incidentType"
              label="Тип проблемы"
              initialValue="downtime"
              rules={[{ required: true }]}
            >
              <Select options={incidentOptions} />
            </Form.Item>
            <Form.Item name="downtimeHours" label="Часы простоя">
              <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="blockingFlag" valuePropName="checked" initialValue>
              <Checkbox>Блокировать оплату до закрытия</Checkbox>
            </Form.Item>
          </>
        ) : (
          <>
            <Checkbox
              checked={registerIncident}
              onChange={(e) => setRegisterIncident(e.target.checked)}
            >
              Зарегистрировать инцидент
            </Checkbox>
            {registerIncident ? (
              <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
                <Select
                  value={incidentType}
                  onChange={setIncidentType}
                  options={incidentOptions}
                  style={{ width: '100%' }}
                />
                <Form.Item name="downtimeHours" label="Часы простоя" style={{ marginBottom: 0 }}>
                  <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="blockingFlag" valuePropName="checked" initialValue={false} style={{ marginBottom: 0 }}>
                  <Checkbox>Блокировать оплату</Checkbox>
                </Form.Item>
              </Space>
            ) : null}
          </>
        )}

        <Form.Item label="Вложение" style={{ marginTop: 12 }}>
          <Upload disabled beforeUpload={() => false} maxCount={1}>
            <Space>
              <UploadOutlined />
              <Text type="secondary">Прикрепление файлов — в следующей версии</Text>
            </Space>
          </Upload>
        </Form.Item>

        {descriptor && !descriptor.opensIncident ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Маршрут #{routeId}
            {descriptor.executionStopUid ? ` · точка ${descriptor.executionStopUid.slice(0, 8)}…` : ''}
          </Text>
        ) : null}
      </Form>
    </Modal>
  );
};

export default ExecutionActionModal;

/** Resolve action sequence for API calls. */
export function resolveActionSequence(descriptor: OperationalActionDescriptor): StopExecutionAction[] {
  if (descriptor.compositeActions?.length) return descriptor.compositeActions;
  if (descriptor.internalAction) return [descriptor.internalAction];
  return [];
}
